/**
 * Wallet tab (ENG-24804).
 *
 * Factory-level view of the vendor's facility wallets. "Vendor level" means
 * factory level: vendor ↔ factory is one-to-one and a factory has many
 * facilities, each with its own Gringotts wallet.
 *
 * Requires a specific factory — the same rule the header wallet icon uses
 * (Header.js), because a wallet only exists per facility and "All Factories"
 * has no meaningful aggregate.
 *
 * Balances come from the VIP per-facility wallet read, the same source the
 * header popup uses.
 *
 * Clicking a facility row opens its Gringotts ledger (ENG-27315). Only rows
 * with a wallet are clickable — a facility with no wallet has no ledger.
 *
 * Open advance (ENG-27316 / ENG-27799) adds two cards, the ageing card and
 * the entries section, from VIP's active (Open + Expired) entries. Where VIP doesn't offer
 * open advance (503 / 404) none of it renders and the tab is unchanged.
 */
import { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import { UserContext } from "@/context/UserContext";
import {
  useFacilityDetails,
  useFactoryWallets,
  useWalletEntries,
} from "@/pages/Payments/api/walletQueries";
import type { WalletEntry } from "@/pages/Payments/types/wallet";
import {
  advanceHeldTotal,
  nearingExpiryTotal,
  nettedThisMonthTotal,
  openAdvanceTotal,
} from "@/pages/Payments/utils/openAdvance";
import ExpiryAgeingCard from "@/pages/Payments/components/ExpiryAgeingCard";
import OpenAdvanceEntries from "@/pages/Payments/components/OpenAdvanceEntries";
import RefundUtrDialog from "@/pages/Payments/modals/RefundUtrDialog";
import { usePermissions } from "@/context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "@/constants/bouncerPermissions";
import type { VendorWalletBalance } from "@/pages/Payments/api/walletQueries";
import { formatAmount } from "@/pages/Payments/utils/formatters";
import { V2_COLORS } from "../constants/colors";
import LoaderV2 from "@/pages/InvoiceManagement/components/LoaderV2";
import { useIsMobile } from "@/hooks/useIsMobile";
import WalletLedgerModal from "@/pages/Payments/modals/WalletLedgerModal";
import MobileCard from "@/components/mobile/MobileCard";
import FieldRow from "@/components/mobile/FieldRow";

const CREDIT_COLOR = V2_COLORS.success;
const DEBIT_COLOR = V2_COLORS.error;

/** A facility row: its balance when a wallet exists and the fetch succeeded. */
export interface WalletFacilityRow {
  id: number;
  name: string;
  walletId: number | null;
  wallet: VendorWalletBalance | null;
}

/** Sums the loaded balances. Facilities without a wallet contribute nothing. */
export function sumWalletTotals(rows: WalletFacilityRow[]) {
  return rows.reduce(
    (acc, row) => {
      if (!row.wallet) return acc;
      return {
        balance: acc.balance + (row.wallet.balance ?? 0),
        credit: acc.credit + (row.wallet.total_credit ?? 0),
        debit: acc.debit + (row.wallet.total_debit ?? 0),
      };
    },
    { balance: 0, credit: 0, debit: 0 },
  );
}

function SummaryCard({
  title,
  amount,
  color,
  sub,
  tooltip,
}: {
  title: string;
  amount: number;
  color: string;
  sub: string;
  tooltip?: string;
}) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: "8px",
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        minHeight: 96,
      }}
    >
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography
          fontSize="11px"
          fontWeight={500}
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
        >
          {title}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <InfoOutlined aria-label={tooltip} sx={{ fontSize: 14, color: "text.secondary" }} />
          </Tooltip>
        )}
      </Box>
      <Typography fontWeight={700} fontSize="22px" color={color} mt={1.25}>
        {formatAmount(amount)}
      </Typography>
      <Typography fontSize="12px" color="text.secondary" mt={0.25}>
        {sub}
      </Typography>
    </Box>
  );
}

/** Dash for a facility with no wallet, amount otherwise. */
function CellAmount({ row, field, color }: { row: WalletFacilityRow; field: keyof VendorWalletBalance; color?: string }) {
  if (!row.wallet) {
    return (
      <Typography component="span" fontSize="13px" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Typography component="span" fontSize="13px" fontWeight={600} color={color}>
      {formatAmount(row.wallet[field] as number)}
    </Typography>
  );
}

function RowStatusNote({ row }: { row: WalletFacilityRow }) {
  if (row.walletId == null) {
    return (
      <Typography fontSize="12px" color="text.secondary">
        No wallet yet
      </Typography>
    );
  }
  if (!row.wallet) {
    return (
      <Typography fontSize="12px" color="error">
        Balance unavailable
      </Typography>
    );
  }
  return null;
}

export default function Wallet() {
  const isMobile = useIsMobile();
  const [ledgerFacility, setLedgerFacility] = useState<WalletFacilityRow | null>(null);
  const [refundEntry, setRefundEntry] = useState<WalletEntry | null>(null);
  const { hasPermission } = usePermissions();
  // Fixed for the mount: expiry and "this month" must not drift between renders.
  const today = useMemo(() => new Date(), []);
  const { invoiceFactory } = useContext(UserContext) ?? {};
  const factoryCode =
    invoiceFactory?.item_code && invoiceFactory.item_code !== "all"
      ? (invoiceFactory.item_code as string)
      : undefined;

  const {
    data: facilities,
    isInitialLoading: facilitiesLoading,
    isError: facilitiesError,
  } = useFacilityDetails(factoryCode);

  // Only facilities with a provisioned wallet are worth a balance fetch.
  const walletFacilityIds = useMemo(
    () => (facilities ?? []).filter((f) => f.walletId != null).map((f) => f.id),
    [facilities],
  );
  const walletResults = useFactoryWallets(walletFacilityIds);

  // Open advance. `enabled: false` means VIP doesn't offer it here (503 / 404):
  // nothing extra renders and the tab stays as it was.
  const { data: openAdvance, isError: openAdvanceError } = useWalletEntries(factoryCode);
  const openAdvanceEntries = openAdvance?.enabled ? openAdvance.entries : null;
  // Per-facility totals arrive only from a VIP that returns them (ENG-27892);
  // they're read inside the open-advance block below, so only when it's on.
  const facilityTotals = openAdvance?.facilities;

  // isInitialLoading, not isLoading — a disabled query in TanStack Query v4
  // reports isLoading:true / fetchStatus:idle, which would pin the loader.
  const isLoading = facilitiesLoading || walletResults.some((r) => r.isInitialLoading);

  // Computed inline rather than memoised: useQueries returns a fresh array every
  // render, so any dependency list would either be a lie or recompute anyway.
  // One factory's facility list is small enough that this costs nothing.
  const balanceByFacilityId = new Map<number, VendorWalletBalance>();
  walletResults.forEach((result, i) => {
    if (result.data) {
      // vendor_facility_id is the primary key; the requested id is an index-based
      // fallback for a response that omits it (same rule as VendorWalletModal).
      balanceByFacilityId.set(result.data.vendor_facility_id ?? walletFacilityIds[i], result.data);
    }
  });
  const rows: WalletFacilityRow[] = (facilities ?? []).map((f) => ({
    id: f.id,
    name: f.name,
    walletId: f.walletId,
    wallet: balanceByFacilityId.get(f.id) ?? null,
  }));

  const totals = sumWalletTotals(rows);
  // The balance aggregates only facilities whose wallet actually loaded, so the
  // card says how many that was rather than implying it covers every facility.
  const loadedCount = rows.filter((r) => r.wallet).length;
  const walletCountLabel = `across ${loadedCount} of ${rows.length} ${
    rows.length === 1 ? "facility" : "facilities"
  }`;

  // "across N facilities" only where there is more than one to be across.
  const facilitiesLabel = rows.length > 1 ? `across ${rows.length} facilities` : "";
  const withFacilities = (text: string) => [text, facilitiesLabel].filter(Boolean).join(" · ");

  const cards: { title: string; amount: number; color: string; sub: string; tooltip?: string }[] = [
    {
      title: "Wallet balance",
      amount: totals.balance,
      color: V2_COLORS.primary,
      sub: walletCountLabel,
    },
  ];
  // Order agreed with Product: Open advance · Advance held · Netted this month ·
  // Nearing expiry. The middle two need VIP's per-facility totals.
  if (openAdvanceEntries) {
    cards.push({
      title: "Open advance",
      amount: openAdvanceTotal(openAdvanceEntries),
      color: V2_COLORS.primary,
      sub: withFacilities("held as credit"),
      tooltip: "Each facility's credit only reduces payments on that facility.",
    });
    if (facilityTotals) {
      cards.push(
        {
          title: "Advance held",
          amount: advanceHeldTotal(facilityTotals),
          color: V2_COLORS.info,
          sub: withFacilities("PO advance not yet adjusted"),
          tooltip:
            "PO advance credited when a PO was acknowledged and not yet adjusted against its invoices. Credited isn't always paid out: the advance payment may still be pending.",
        },
        {
          title: "Netted this month",
          amount: nettedThisMonthTotal(facilityTotals),
          color: V2_COLORS.success,
          sub: withFacilities("set off against invoices"),
          tooltip: "Held credit set off against invoices since the 1st of this month (IST).",
        },
      );
    }
    cards.push({
      title: "Nearing expiry",
      amount: nearingExpiryTotal(openAdvanceEntries, today),
      color: V2_COLORS.warning,
      sub: withFacilities("expires within 10 days"),
    });
  }
  const facilityNames = new Map(rows.map((r) => [r.id, r.name]));
  // The per-facility split behind the Open advance card (agreed 2026-09-29):
  // VIP's spendable balance per facility. A facility VIP doesn't list, or lists
  // without the figure, has none.
  const openAdvanceByFacility =
    openAdvanceEntries && facilityTotals
      ? new Map(facilityTotals.map((f) => [f.vendor_facility_id, f.open_advance_balance]))
      : null;
  const openAdvanceAmount = (row: WalletFacilityRow) => (
    <Typography component="span" fontSize="13px" fontWeight={600} color={V2_COLORS.primary}>
      {formatAmount(openAdvanceByFacility?.get(row.id) ?? 0)}
    </Typography>
  );
  const tableHeadings = [
    "Facility",
    "Balance",
    ...(openAdvanceByFacility ? ["Open advance"] : []),
    "Total credit",
    "Total debit",
    "",
  ];

  // Opening the ledger keeps the chosen facility in state rather than only its
  // id, so the modal can show the name immediately without a second lookup.
  const handleRowClick = (row: WalletFacilityRow) => {
    setLedgerFacility(row);
  };

  if (!factoryCode) {
    return (
      <Box p={3} textAlign="center">
        <Typography fontSize="14px" color="text.secondary">
          Select a factory to view its wallet balances.
        </Typography>
      </Box>
    );
  }

  if (isLoading) return <LoaderV2 />;

  if (facilitiesError) {
    return (
      <Box p={3} textAlign="center">
        <Typography fontSize="14px" color="error">
          Couldn&apos;t load facilities for this factory. Please try again.
        </Typography>
      </Box>
    );
  }

  // Same wording as the header wallet popup (VendorWalletModal), so the two
  // never give different reasons for one empty wallet (ENG-28342).
  if (rows.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography fontSize="14px" color="text.secondary">
          No wallet data found for this factory.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 2 : 3} sx={{ overflowY: "auto" }}>
      {/* Open-advance cards appear only where VIP offers open advance (see `cards`). */}
      <Box
        display="grid"
        gridTemplateColumns={isMobile ? "1fr" : "repeat(auto-fill, minmax(240px, 1fr))"}
        gap={2.5}
        mb={3}
      >
        {cards.map((card) => (
          <SummaryCard
            key={card.title}
            title={card.title}
            amount={card.amount}
            color={card.color}
            sub={card.sub}
            tooltip={card.tooltip}
          />
        ))}
      </Box>

      <Box display="flex" gap={1} alignItems="center" mb={2}>
        <Typography fontWeight={700} fontSize="16px">
          Facilities
        </Typography>
        <Typography fontSize="12px" color="text.secondary">
          ({rows.length})
        </Typography>
      </Box>

      {isMobile ? (
        <Box>
          {rows.map((row) => (
            <MobileCard
              key={row.id}
              flat
              onClick={row.walletId != null ? () => handleRowClick(row) : undefined}
            >
              <Box py={1}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                  <Typography fontSize="13px" fontWeight={600}>
                    {row.name}
                  </Typography>
                  <CellAmount row={row} field="balance" color={V2_COLORS.primary} />
                </Box>
                <RowStatusNote row={row} />
                {openAdvanceByFacility && (
                  <Box mb={0.5}>
                    <FieldRow inline label="Open advance" value={openAdvanceAmount(row)} />
                  </Box>
                )}
                {row.wallet && (
                  <Box display="flex" gap={3} alignItems="center">
                    <FieldRow
                      inline
                      label="Credit"
                      value={<CellAmount row={row} field="total_credit" color={CREDIT_COLOR} />}
                    />
                    <FieldRow
                      inline
                      label="Debit"
                      value={<CellAmount row={row} field="total_debit" color={DEBIT_COLOR} />}
                    />
                    {row.walletId != null && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRowClick(row);
                        }}
                        sx={{
                          ml: "auto",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "12px",
                          minWidth: 0,
                          p: 0,
                        }}
                      >
                        View ledger
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </MobileCard>
          ))}
        </Box>
      ) : (
        <TableContainer sx={{ bgcolor: "background.paper", borderRadius: "8px" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tableHeadings.map((heading, i) => (
                  <TableCell
                    key={heading || "actions"}
                    align={i === 0 ? "left" : "right"}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "text.secondary",
                      bgcolor: "background.paper",
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                // A facility with no wallet has no ledger, so it stays inert.
                const clickable = row.walletId != null;
                return (
                  <TableRow
                    key={row.id}
                    hover={clickable}
                    onClick={clickable ? () => handleRowClick(row) : undefined}
                    sx={{ cursor: clickable ? "pointer" : "default" }}
                  >
                    <TableCell>
                      <Typography fontSize="13px" fontWeight={600}>
                        {row.name}
                      </Typography>
                      <RowStatusNote row={row} />
                    </TableCell>
                    <TableCell align="right">
                      <CellAmount row={row} field="balance" color={V2_COLORS.primary} />
                    </TableCell>
                    {openAdvanceByFacility && <TableCell align="right">{openAdvanceAmount(row)}</TableCell>}
                    <TableCell align="right">
                      <CellAmount row={row} field="total_credit" color={CREDIT_COLOR} />
                    </TableCell>
                    <TableCell align="right">
                      <CellAmount row={row} field="total_debit" color={DEBIT_COLOR} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {clickable && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={(event) => {
                            // The whole row is clickable too — without this the
                            // row handler fires as well and opens twice.
                            event.stopPropagation();
                            handleRowClick(row);
                          }}
                          sx={{ textTransform: "none", fontWeight: 600, fontSize: "13px" }}
                        >
                          View ledger
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {openAdvanceEntries && (
        <Box mt={3}>
          <ExpiryAgeingCard entries={openAdvanceEntries} today={today} />
        </Box>
      )}
      {openAdvanceEntries && (
        <OpenAdvanceEntries
          entries={openAdvanceEntries}
          factoryCode={factoryCode}
          facilityNames={facilityNames}
          today={today}
          canRecordRefund={hasPermission(BOUNCER_PERMISSIONS.WALLET_REFUND_RECORD)}
          onRecordRefund={setRefundEntry}
        />
      )}
      {openAdvanceError && (
        <Typography fontSize="13px" color="error" mt={3}>
          Couldn&apos;t load open advance for this factory. Please try again.
        </Typography>
      )}

      <RefundUtrDialog entry={refundEntry} onClose={() => setRefundEntry(null)} />

      <WalletLedgerModal
        open={ledgerFacility !== null}
        onClose={() => setLedgerFacility(null)}
        facility={
          ledgerFacility && {
            id: ledgerFacility.id,
            name: ledgerFacility.name,
            walletId: ledgerFacility.walletId,
            // Already fetched for the table — hand them over so the ledger
            // header shows figures immediately rather than dashes.
            balance: ledgerFacility.wallet?.balance,
            totalCredit: ledgerFacility.wallet?.total_credit,
            totalDebit: ledgerFacility.wallet?.total_debit,
          }
        }
      />
    </Box>
  );
}
